import { format, formatDistanceToNow, formatDistanceToNowStrict } from 'date-fns';

type DateInput = string | Date | null | undefined;

const BARE_ISO_UTC_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?$/;
const ISO_WITH_TIMEZONE_REGEX = /(Z|[+-]\d{2}:\d{2})$/i;

function parseDate(date: DateInput): Date | null {
  if (!date) return null;
  if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date;

  const normalized =
    date.includes('T') && !ISO_WITH_TIMEZONE_REGEX.test(date) && BARE_ISO_UTC_REGEX.test(date)
      ? `${date}Z`
      : date;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getUserLocale(): string {
  if (typeof navigator === 'undefined') return 'en-US';
  return navigator.languages?.[0] || navigator.language || 'en-US';
}

export function getUserTimezone(): string {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('user-timezone');
    if (stored) return stored;
  }

  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function setUserTimezone(timezone: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem('user-timezone', timezone);
}

export function formatDate(date: string | Date, formatPattern = 'PP'): string {
  const parsed = parseDate(date);
  if (!parsed) return '—';
  return format(parsed, formatPattern);
}

export function formatRelativeTime(date: string | Date): string {
  const parsed = parseDate(date);
  if (!parsed) return '—';
  return formatDistanceToNow(parsed, { addSuffix: true });
}

export function formatCountdown(date: string | Date): string {
  const parsed = parseDate(date);
  if (!parsed) return '—';
  return formatDistanceToNowStrict(parsed, { addSuffix: true });
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours === 0 && minutes === 0) return `${seconds}s`;
  if (hours === 0) return `${minutes}m ${seconds}s`;
  return `${hours}h ${minutes}m ${seconds}s`;
}

export function formatTimestamp(date: string | Date): string {
  const parsed = parseDate(date);
  if (!parsed) return '—';
  return format(parsed, 'PPpp');
}

export function formatDateTime(date: DateInput): string {
  const parsed = parseDate(date);
  if (!parsed) return '—';
  return format(parsed, 'PPpp');
}

export function formatTime(date: DateInput): string {
  const parsed = parseDate(date);
  if (!parsed) return '—';
  return format(parsed, 'p');
}
